<template>
  <div>
    <!-- 赋像散订单编辑组件的内容会通过props传入并在父组件中展示 -->
  </div>
</template>

<script setup>
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { nextTick } from 'vue';
import { setupOpticalFormInputs, fillDefaultZeroFields, convertVisionFieldsInForm } from '@/utils/opticalDataUtil';
import { isAuthRedirecting } from '@/utils/authRedirect';

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  // 是否显示移动端样式
  isMobile: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'cancel']);

// 获取验配单位列表（用于编辑弹窗下拉）
const fetchOptometryClinicOptions = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.data.code === '200' && response.data.data) {
      const clinics = response.data.data || [];
      const unique = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      return unique;
    }
  } catch (e) {
    if (e?.response?.status === 401 || isAuthRedirecting()) {
      return null;
    }
    console.error('获取验配单位列表失败', e);
  }
  return [];
};

// 编辑赋像散订单
const handleEditFuxiangsan = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 先获取验配单位列表供下拉使用
    const optometryClinicOptions = await fetchOptometryClinicOptions();
    if (optometryClinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const currentCompany = (row.company || '').trim();
    const hasCurrentInList = currentCompany && optometryClinicOptions.includes(currentCompany);
    let companyOptionsHtml = optometryClinicOptions.map(c =>
      `<option value="${String(c || '').replace(/"/g, '&quot;')}" ${currentCompany === c ? 'selected' : ''}>${String(c || '').replace(/</g, '&lt;')}</option>`
    ).join('');
    if (currentCompany && !hasCurrentInList) {
      companyOptionsHtml = `<option value="${currentCompany.replace(/"/g, '&quot;')}" selected>${currentCompany.replace(/</g, '&lt;')}</option>` + companyOptionsHtml;
    }

    await ElMessageBox.confirm(
        `<div class="edit-order-form">
        <!-- 第一部分：订单基本信息 -->
        <div class="form-section basic-info">
          <h3>订单基本信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>序号：</label>
              <input type="text" class="el-input__inner" value="${displayId}" readonly>
              <input type="hidden" id="edit-fuxiangsan-id" value="${row.id || ''}">
            </div>
            <div class="form-item">
              <label>订单号：</label>
              <input type="text" class="el-input__inner" value="${row.orderNumber || ''}" id="edit-fuxiangsan-orderNumber" readonly>
            </div>
            <div class="form-item">
              <label>用户ID：</label>
              <input type="text" class="el-input__inner" value="${row.userId || ''}" id="edit-fuxiangsan-userId" readonly>
            </div>
            <div class="form-item">
              <label>订单日期<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-fuxiangsan-orderDate" readonly>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>订单状态：</label>
              <select class="el-input__inner" id="edit-fuxiangsan-status">
                <option value="Exported" ${row.status === 'Exported' ? 'selected' : ''}>已导出</option>
                <option value="Noexported" ${row.status === 'Noexported' ? 'selected' : ''}>未导出</option>
              </select>
            </div>
            <div class="form-item">
              <label>订单类型：</label>
              <input type="text" class="el-input__inner" value="${row.orderType || '散订单'}" id="edit-fuxiangsan-orderType" readonly>
            </div>
            <div class="form-item">
              <label>患者姓名<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.patientName || ''}" id="edit-fuxiangsan-patientName" required>
            </div>
            <div class="form-item">
              <label>年龄<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.age || ''}" id="edit-fuxiangsan-age">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>验光师：</label>
              <input type="text" class="el-input__inner" value="${row.optometrist || ''}" id="edit-fuxiangsan-optometrist">
            </div>
            <div class="form-item">
              <label>地区：</label>
              <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-fuxiangsan-region">
            </div>
            <div class="form-item">
              <label>订单型号<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-fuxiangsan-lensModel">
            </div>
            <div class="form-item">
              <label>验配单位<span class="required-star">*</span>：</label>
              <select class="el-input__inner" id="edit-fuxiangsan-company">
                <option value="">请选择验配单位</option>
                ${companyOptionsHtml}
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>采购单位<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-fuxiangsan-purchasingUnits">
            </div>
          </div>
        </div>

        <!-- 第二部分：常规验光 -->
        <div class="form-section two-columns">
          <!-- 常规验光 -->
          <div class="column regular-refraction-column">
            <h3>常规验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdSphere || ''}" id="edit-fuxiangsan-regularOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdCylinder || ''}" id="edit-fuxiangsan-regularOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdAxis || ''}" id="edit-fuxiangsan-regularOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdVision || ''}" id="edit-fuxiangsan-regularOdVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odQuantity || ''}" id="edit-fuxiangsan-odQuantity">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsSphere || ''}" id="edit-fuxiangsan-regularOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsCylinder || ''}" id="edit-fuxiangsan-regularOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsAxis || ''}" id="edit-fuxiangsan-regularOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner " value="${row.regularOsVision || ''}" id="edit-fuxiangsan-regularOsVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osQuantity || ''}" id="edit-fuxiangsan-osQuantity">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner " value="${row.regularBinocularVision || ''}" id="edit-fuxiangsan-regularBinocularVision">
              </div>
            </div>
          </div>

          <!-- 散瞳验光 -->
          <div class="column cycloplegic-refraction-column">
            <h3>散瞳验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdSphere || ''}" id="edit-fuxiangsan-cycloplegicOdSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdCylinder || ''}" id="edit-fuxiangsan-cycloplegicOdCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdAxis || ''}" id="edit-fuxiangsan-cycloplegicOdAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdVision || ''}" id="edit-fuxiangsan-cycloplegicOdVision"></div></div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsSphere || ''}" id="edit-fuxiangsan-cycloplegicOsSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsCylinder || ''}" id="edit-fuxiangsan-cycloplegicOsCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsAxis || ''}" id="edit-fuxiangsan-cycloplegicOsAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsVision || ''}" id="edit-fuxiangsan-cycloplegicOsVision"></div></div>
              </div>
            </div>
          </div>

          <!-- 试戴片验光 -->
          <div class="column trial-refraction-column">
            <h3>试戴片验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdSphere || ''}" id="edit-fuxiangsan-superOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdCylinder || ''}" id="edit-fuxiangsan-superOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdAxis || ''}" id="edit-fuxiangsan-superOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner " value="${row.superOdVision || ''}" id="edit-fuxiangsan-superOdVision">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsSphere || ''}" id="edit-fuxiangsan-superOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsCylinder || ''}" id="edit-fuxiangsan-superOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsAxis || ''}" id="edit-fuxiangsan-superOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsVision || ''}" id="edit-fuxiangsan-superOsVision">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.superBinocularVision || ''}" id="edit-fuxiangsan-superBinocularVision">
              </div>
            </div>
          </div>
        </div>

        <!-- 第三部分：角膜曲率和眼镜参数 -->
        <div class="form-section two-columns">
          <!-- 角膜曲率 -->
          <div class="column">
            <h3>角膜曲率</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odK1 || ''}" id="edit-fuxiangsan-odK1">
                  </div>
                  <div class="form-item">
                    <label>K2：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odK2 || ''}" id="edit-fuxiangsan-odK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odAxialLength || ''}" id="edit-fuxiangsan-odAxialLength">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osK1 || ''}" id="edit-fuxiangsan-osK1">
                  </div>
                  <div class="form-item">
                    <label>K2：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osK2 || ''}" id="edit-fuxiangsan-osK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osAxialLength || ''}" id="edit-fuxiangsan-osAxialLength">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 眼镜参数 -->
          <div class="column">
            <h3>眼镜参数</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odPupilHeight || ''}" id="edit-fuxiangsan-odPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odPupilDistance || ''}" id="edit-fuxiangsan-odPupilDistance">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osPupilHeight || ''}" id="edit-fuxiangsan-osPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osPupilDistance || ''}" id="edit-fuxiangsan-osPupilDistance">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼瞳距：</label>
                <input type="text" class="el-input__inner numeric-input" value="${row.binocularPupilDistance || ''}" id="edit-fuxiangsan-binocularPupilDistance">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>镜框编号：</label>
                <input type="text" class="el-input__inner" value="${row.frameNumber || ''}" id="edit-fuxiangsan-frameNumber">
              </div>
              <div class="form-item">
                <label>镜框类型：</label>
                <select class="el-input__inner" id="edit-fuxiangsan-frameType">
                  <option value="原片" ${row.frameType === '来框加工' || row.frameType === '豪赋镜框加工' ? '' : 'selected'}>原片</option>
                  <option value="来框加工" ${row.frameType === '来框加工' ? 'selected' : ''}>来框加工</option>
                  <option value="豪赋镜框加工" ${row.frameType === '豪赋镜框加工' ? 'selected' : ''}>豪赋镜框加工</option>
                </select>
              </div>
            </div>
          </div>
        </div>
         <!-- 第四部分：眼位功能 -->
        <div class="form-section">
          <h3>眼位</h3>
          <div class="form-row">
            <div class="form-item">
              <label>远：</label>
              <input type="text" class="el-input__inner" value="${row.eyePositionFar || ''}" id="edit-fuxiangsan-eyePositionFar">
            </div>
            <div class="form-item">
              <label>近：</label>
              <input type="text" class="el-input__inner" value="${row.eyePositionNear || ''}" id="edit-fuxiangsan-eyePositionNear">
            </div>
          </div>
        </div>

        <!-- 第五部分：邮寄信息 -->
        <div class="form-section delivery-info">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-fuxiangsan-addressee">
            </div>
            <div class="form-item">
              <label>联系电话<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-fuxiangsan-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-fuxiangsan-trackingNumber">
            </div>
            <div class="form-item">
              <label>邮寄日期：</label>
              <input type="text" class="el-input__inner" value="${row.deliveryDate || ''}" id="edit-fuxiangsan-deliveryDate">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址<span class="required-star">*</span>：</label>
              <textarea class="el-textarea__inner" id="edit-fuxiangsan-shippingAddress">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-fuxiangsan-remarks">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑其他订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    if (isAuthRedirecting()) {
      return;
    }

    // 即时绑定数字校验与键盘导航
    nextTick(() => {
      setupOpticalFormInputs('edit-fuxiangsan');

      const maxlengthMap = {
        'edit-fuxiangsan-patientName': 30,
        'edit-fuxiangsan-age': 10,
        'edit-fuxiangsan-optometrist': 10,
        'edit-fuxiangsan-region': 55,
        'edit-fuxiangsan-lensModel': 30,
        'edit-fuxiangsan-company': 30,
        'edit-fuxiangsan-purchasingUnits': 30,
        'edit-fuxiangsan-odPupilDistance': 10,
        'edit-fuxiangsan-osPupilDistance': 10,
        'edit-fuxiangsan-frameNumber': 20,
        'edit-fuxiangsan-eyePositionFar': 40,
        'edit-fuxiangsan-eyePositionNear': 40,
        'edit-fuxiangsan-addressee': 10,
        'edit-fuxiangsan-phone': 15
      };
      Object.entries(maxlengthMap).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    });

    // 获取所有表单数据
    const fuxiangsanOrderData = {
      id: document.getElementById('edit-fuxiangsan-id').value,
      userId: document.getElementById('edit-fuxiangsan-userId').value,
      orderType: document.getElementById('edit-fuxiangsan-orderType').value,
      orderNumber: document.getElementById('edit-fuxiangsan-orderNumber').value,
      status: document.getElementById('edit-fuxiangsan-status').value,
      orderDate: document.getElementById('edit-fuxiangsan-orderDate').value,
      patientName: document.getElementById('edit-fuxiangsan-patientName').value,
      age: document.getElementById('edit-fuxiangsan-age').value || null,
      optometrist: document.getElementById('edit-fuxiangsan-optometrist').value || null,
      region: document.getElementById('edit-fuxiangsan-region').value || null,
      lensModel: document.getElementById('edit-fuxiangsan-lensModel').value,
      company: document.getElementById('edit-fuxiangsan-company').value || null,
      purchasingUnits: document.getElementById('edit-fuxiangsan-purchasingUnits').value || null,

      // 常规验光 - 右眼
      regularOdSphere: document.getElementById('edit-fuxiangsan-regularOdSphere').value || null,
      regularOdCylinder: document.getElementById('edit-fuxiangsan-regularOdCylinder').value || null,
      regularOdAxis: document.getElementById('edit-fuxiangsan-regularOdAxis').value || null,
      regularOdVision: document.getElementById('edit-fuxiangsan-regularOdVision').value || null,
      // 常规验光 - 左眼
      regularOsSphere: document.getElementById('edit-fuxiangsan-regularOsSphere').value || null,
      regularOsCylinder: document.getElementById('edit-fuxiangsan-regularOsCylinder').value || null,
      regularOsAxis: document.getElementById('edit-fuxiangsan-regularOsAxis').value || null,
      regularOsVision: document.getElementById('edit-fuxiangsan-regularOsVision').value || null,
      regularBinocularVision: document.getElementById('edit-fuxiangsan-regularBinocularVision').value || null,
      // 散瞳验光
      cycloplegicOdSphere: document.getElementById('edit-fuxiangsan-cycloplegicOdSphere').value || null,
      cycloplegicOdCylinder: document.getElementById('edit-fuxiangsan-cycloplegicOdCylinder').value || null,
      cycloplegicOdAxis: document.getElementById('edit-fuxiangsan-cycloplegicOdAxis').value || null,
      cycloplegicOdVision: document.getElementById('edit-fuxiangsan-cycloplegicOdVision').value || null,
      cycloplegicOsSphere: document.getElementById('edit-fuxiangsan-cycloplegicOsSphere').value || null,
      cycloplegicOsCylinder: document.getElementById('edit-fuxiangsan-cycloplegicOsCylinder').value || null,
      cycloplegicOsAxis: document.getElementById('edit-fuxiangsan-cycloplegicOsAxis').value || null,
      cycloplegicOsVision: document.getElementById('edit-fuxiangsan-cycloplegicOsVision').value || null,
      // 赋像散验光 - 右眼
      superOdSphere: document.getElementById('edit-fuxiangsan-superOdSphere').value || null,
      superOdCylinder: document.getElementById('edit-fuxiangsan-superOdCylinder').value || null,
      superOdAxis: document.getElementById('edit-fuxiangsan-superOdAxis').value || null,
      superOdVision: document.getElementById('edit-fuxiangsan-superOdVision').value || null,
      odQuantity: document.getElementById('edit-fuxiangsan-odQuantity').value || null,
      // 赋像散验光 - 左眼
      superOsSphere: document.getElementById('edit-fuxiangsan-superOsSphere').value || null,
      superOsCylinder: document.getElementById('edit-fuxiangsan-superOsCylinder').value || null,
      superOsAxis: document.getElementById('edit-fuxiangsan-superOsAxis').value || null,
      superOsVision: document.getElementById('edit-fuxiangsan-superOsVision').value || null,
      osQuantity: document.getElementById('edit-fuxiangsan-osQuantity').value || null,
      superBinocularVision: document.getElementById('edit-fuxiangsan-superBinocularVision').value || null,
      frameNumber: document.getElementById('edit-fuxiangsan-frameNumber').value || null,
      frameType: document.getElementById('edit-fuxiangsan-frameType').value || null,
      // 角膜曲率 - 右眼
      odK1: document.getElementById('edit-fuxiangsan-odK1').value || null,
      odK2: document.getElementById('edit-fuxiangsan-odK2').value || null,
      odAxialLength: document.getElementById('edit-fuxiangsan-odAxialLength').value || null,
      // 角膜曲率 - 左眼
      osK1: document.getElementById('edit-fuxiangsan-osK1').value || null,
      osK2: document.getElementById('edit-fuxiangsan-osK2').value || null,
      osAxialLength: document.getElementById('edit-fuxiangsan-osAxialLength').value || null,
      // 眼镜参数 - 右眼
      odPupilHeight: document.getElementById('edit-fuxiangsan-odPupilHeight').value || null,
      odPupilDistance: document.getElementById('edit-fuxiangsan-odPupilDistance').value || null,
      // 眼镜参数 - 左眼
      osPupilHeight: document.getElementById('edit-fuxiangsan-osPupilHeight').value || null,
      osPupilDistance: document.getElementById('edit-fuxiangsan-osPupilDistance').value || null,
      binocularPupilDistance: document.getElementById('edit-fuxiangsan-binocularPupilDistance').value || null,
      // 眼位
      eyePositionFar: document.getElementById('edit-fuxiangsan-eyePositionFar').value || null,
      eyePositionNear: document.getElementById('edit-fuxiangsan-eyePositionNear').value || null,

      // 邮寄信息
      addressee: document.getElementById('edit-fuxiangsan-addressee').value || null,
      phone: document.getElementById('edit-fuxiangsan-phone').value || null,
      trackingNumber: document.getElementById('edit-fuxiangsan-trackingNumber').value || null,
      deliveryDate: document.getElementById('edit-fuxiangsan-deliveryDate').value || null,
      shippingAddress: document.getElementById('edit-fuxiangsan-shippingAddress').value || null,
      remarks: document.getElementById('edit-fuxiangsan-remarks').value || null
    };

    const validateFuxiangsanFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 定义验证规则
    const fuxiangsanValidationRules = [
      { field: 'patientName', message: '请输入患者姓名' },
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'purchasingUnits', message: '请输入采购单位' },
      { field: 'age', message: '请输入年龄' },
      { field: 'regularOdSphere', message: '请输入常规验光右眼球镜' },
      { field: 'regularOdCylinder',message: '请输入常规验光右眼柱镜' },
      { field:'regularOdAxis', message: '请输入常规验光右眼轴位' },
      { field: 'regularOsSphere', message: '请输入常规验光左眼球镜' },
      { field:'regularOsCylinder', message: '请输入常规验光左眼柱镜' },
      { field:'regularOsAxis', message: '请输入常规验光左眼轴位' },
      { field:'addressee', message: '请输入收件人姓名'},
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'phone', message: '请输入联系电话'}
    ];

    // 默认值填充
    fillDefaultZeroFields(fuxiangsanOrderData);
    // 编辑保存前：对数视力 → 小数视力（方案B）
    convertVisionFieldsInForm(fuxiangsanOrderData);

    // 执行验证
    if (!validateFuxiangsanFields(fuxiangsanOrderData, fuxiangsanValidationRules)) {
      return;
    }

    console.log('Sending fuxiangsan order data:', fuxiangsanOrderData);

    const response = await axios.put(`/api/v1/admin/scatterorder`, fuxiangsanOrderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

// 显示赋像散订单详情
const showFuxiangsanOrderDetails = (row) => {
  // 将所有属性格式化为易读的表格
  let content = '<div class="order-details">';
  content += '<h3>订单基本信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>订单号</td><td>${row.orderNumber || '-'}</td></tr>`;
  content += `<tr><td>订单类型</td><td>${row.orderType || '-'}</td></tr>`;
  content += `<tr><td>订单状态</td><td>${row.status === 'Exported' ? '已导出' : '未导出'}</td></tr>`;
  content += `<tr><td>患者姓名</td><td>${row.patientName || '-'}</td></tr>`;
  content += `<tr><td>年龄</td><td>${row.age || '-'}</td></tr>`;
  content += `<tr><td>验光师</td><td>${row.optometrist || '-'}</td></tr>`;
  content += `<tr><td>地区</td><td>${row.region || '-'}</td></tr>`;
  content += `<tr><td>订单型号</td><td>${row.lensModel || '-'}</td></tr>`;
  content += `<tr><td>验光单位</td><td>${row.company || '-'}</td></tr>`;
  content += `<tr><td>采购单位</td><td>${row.purchasingUnits || '-'}</td></tr>`;
  content += `<tr><td>创建时间</td><td>${row.createdAt || '-'}</td></tr>`;
  content += `<tr><td>更新时间</td><td>${row.updatedAt || '-'}</td></tr>`;
  content += '</table>';
  
  // 常规验光数据
  content += '<h3>常规验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.regularOdSphere > 0 ? '+' + row.regularOdSphere : row.regularOdSphere || '-'}</td>
              <td>${row.regularOsSphere > 0 ? '+' + row.regularOsSphere : row.regularOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.regularOdCylinder || '-'}</td><td>${row.regularOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.regularOdAxis || '-'}</td><td>${row.regularOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.regularOdVision || '-'}</td><td>${row.regularOsVision || '-'}</td></tr>`;
  content += `<tr><td>数量</td><td>${row.odQuantity || '-'}</td><td>${row.osQuantity || '-'}</td></tr>`;
  content += `<tr><td>双眼视力</td><td colspan="2">${row.regularBinocularVision || '-'}</td></tr>`;
  content += '</table>';
  
  // 散瞳验光数据
  content += '<h3>散瞳验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.cycloplegicOdSphere > 0 ? '+' + row.cycloplegicOdSphere : row.cycloplegicOdSphere || '-'}</td><td>${row.cycloplegicOsSphere > 0 ? '+' + row.cycloplegicOsSphere : row.cycloplegicOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.cycloplegicOdCylinder || '-'}</td><td>${row.cycloplegicOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.cycloplegicOdAxis || '-'}</td><td>${row.cycloplegicOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.cycloplegicOdVision || '-'}</td><td>${row.cycloplegicOsVision || '-'}</td></tr>`;
  content += '</table>';

  // 赋像散验光数据
  content += '<h3>试戴片验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.superOdSphere > 0 ? '+' + row.superOdSphere : row.superOdSphere || '-'}</td>
              <td>${row.superOsSphere > 0 ? '+' + row.superOsSphere : row.superOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.superOdCylinder || '-'}</td><td>${row.superOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.superOdAxis || '-'}</td><td>${row.superOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.superOdVision || '-'}</td><td>${row.superOsVision || '-'}</td></tr>`;
  content += `<tr><td>双眼视力</td><td colspan="2">${row.superBinocularVision || '-'}</td></tr>`;
  content += '</table>';
  
  // 角膜曲率
  content += '<h3>角膜曲率</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>K1</td><td>${row.odK1 || '-'}</td><td>${row.osK1 || '-'}</td></tr>`;
  content += `<tr><td>K2</td><td>${row.odK2 || '-'}</td><td>${row.osK2 || '-'}</td></tr>`;
  content += `<tr><td>眼轴</td><td>${row.odAxialLength || '-'}</td><td>${row.osAxialLength || '-'}</td></tr>`;
  content += '</table>';
  
  // 眼镜参数
  content += '<h3>眼镜参数</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>瞳高</td><td>${row.odPupilHeight || '-'}</td><td>${row.osPupilHeight || '-'}</td></tr>`;
  content += `<tr><td>瞳距</td><td>${row.odPupilDistance || '-'}</td><td>${row.osPupilDistance || '-'}</td></tr>`;
  content += `<tr><td>双眼瞳距</td><td colspan="2">${row.binocularPupilDistance || '-'}</td></tr>`;
  content += `<tr><td>镜框编号</td><td colspan="2">${row.frameNumber || '-'}</td></tr>`;
  content += `<tr><td>镜框类型</td><td colspan="2">${row.frameType || '-'}</td></tr>`;
  content += `<tr><td>眼位(远)</td><td colspan="2">${row.eyePositionFar || '-'}</td></tr>`;
  content += `<tr><td>眼位(近)</td><td colspan="2">${row.eyePositionNear || '-'}</td></tr>`;
  content += '</table>';
  
  // 邮寄信息
  content += '<h3>邮寄信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>收件人</td><td>${row.addressee || '-'}</td></tr>`;
  content += `<tr><td>联系电话</td><td>${row.phone || '-'}</td></tr>`;
  content += `<tr><td>物流编号</td><td>${row.trackingNumber || '-'}</td></tr>`;
  content += `<tr><td>邮寄地址</td><td>${row.shippingAddress || '-'}</td></tr>`;
  content += `<tr><td>邮寄日期</td><td>${row.deliveryDate || '-'}</td></tr>`;
  content += `<tr><td>备注</td><td>${row.remarks || '-'}</td></tr>`;
  content += '</table>';
  
  content += '</div>';
  
  // 添加详情样式
  content += `<style>
    .el-message-box__container{
      display: block;
    }
    .order-details {
      font-size: 14px;
      padding: 0;
    }
    .order-details h3 {
      margin: 15px 0 8px;
      padding-bottom: 5px;
      border-bottom: 1px solid #EBEEF5;
      color: #409EFF;
      font-size: 16px;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }
    .details-table td, .details-table th {
      padding: 8px;
      border: 1px solid #EBEEF5;
      text-align: left;
    }
    .details-table th {
      background-color: #f5f7fa;
      color: #606266;
    }
    .el-message-box.details-dialog {
      width: 50% !important;
      max-width: 1200px !important;
    }
    .el-message-box.details-dialog .el-message-box__content {
      max-height: 70vh;
      overflow-y: auto;
    }
    @media screen and (max-width: 768px) {
      .order-details {
        font-size: 13px;
      }
      .details-table td, .details-table th {
        padding: 5px;
      }
    }
    @media screen and (max-width: 480px) {
      .el-message-box.details-dialog {
       width: 80% !important;
      }
    }
  </style>`;
  
  // 修改调用方式，添加catch来处理取消操作
  ElMessageBox.alert(content, '其他订单详情', {
    dangerouslyUseHTMLString: true,
    customClass: 'details-dialog',
    closeOnClickModal: true,
    distinguishCancelAndClose: true,
  }).catch(() => {
    // 静默处理取消或关闭操作
    console.log('对话框已关闭');
  });
};

// 导出赋像散订单Excel
const handleExportFuxiangsanExcel = async () => {
  try {
    const response = await axios({
      url: '/api/v1/admin/scatterexcelexport',
      method: 'GET',
      params: { orderType: 'fuxiangsan' }, // 添加订单类型过滤
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `其他订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      emit('update');
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 导出选中的赋像散订单
const handleExportSelectedFuxiangsanOrders = async (selectedOrders) => {
  if (selectedOrders.length === 0) {
    ElMessage.warning('请至少选择一条订单');
    return;
  }
  
  // 检查是否包含已导出的订单
  const hasExportedOrders = selectedOrders.some(order => order.status === 'Exported');
  
  if (hasExportedOrders) {
    try {
      await ElMessageBox.confirm('选中的订单中包含已导出的订单，是否继续导出？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });
      // 用户确认导出，继续执行
    } catch (e) {
      // 用户取消导出
      return;
    }
  }
  
  // 导出选中的赋像散订单
  try {
    const orderIds = selectedOrders.map(order => order.id);
    
    const response = await axios({
      url: '/api/v1/admin/exportSelectedScatterOrders',
      method: 'POST',
      data: orderIds,
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `其他订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      emit('update');
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 暴露函数给父组件使用
defineExpose({
  handleEditFuxiangsan,
  showFuxiangsanOrderDetails,
  handleExportFuxiangsanExcel,
  handleExportSelectedFuxiangsanOrders
});
</script> 
